import { CsvDataRow } from 'components/TotemCsvInputButton';
import {
    REQUIRED_UPDATE_FIELDS,
    OPTIONAL_UPDATE_FIELDS,
    NUMERIC_FIELDS,
    BOOLEAN_FIELDS,
    ARRAY_FIELDS,
} from 'pages/Sites/constants/csvFields';
import { isFalse, isTrue } from './utils';

const formatField = (value: string, fieldName: string) => {
    if (value === '') return undefined;

    if (BOOLEAN_FIELDS.includes(fieldName)) {
        const lowered = value.toLowerCase().trim();
        if (isTrue(lowered)) return true;
        if (isFalse(lowered)) return false;
        return undefined;
    }
    if (NUMERIC_FIELDS.includes(fieldName)) {
        const numValue = Number(value);
        return !isNaN(numValue) && Number.isInteger(numValue) ? numValue : undefined;
    }
    if (ARRAY_FIELDS.includes(fieldName)) {
        try {
            return JSON.parse(value);
        } catch {
            return undefined;
        }
    }
    return value;
};

const handleNestedField = (obj: Record<string, any>, path: string[], value: any) => {
    let current = obj;
    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (!current[key]) current[key] = {};
        current = current[key];
    }
    current[path[path.length - 1]] = value;
};

const handleUrbantzMetadata = (site: Record<string, any>, fieldPath: string[], value: string | undefined) => {
    const [, service, day, weekType] = fieldPath;

    // Map days to array indices
    const dayIndex = {
        monday: 0,
        tuesday: 1,
        wednesday: 2,
        thursday: 3,
        friday: 4,
    }[day];

    if (dayIndex === undefined) return;

    // Initialize urbantzMetadata structure if needed
    if (!site.urbantzMetadata) {
        site.urbantzMetadata = {};
    }

    if (!site.urbantzMetadata[service]) {
        site.urbantzMetadata[service] = new Array(5).fill(undefined);
    }

    // Initialize the day's object if needed
    if (!site.urbantzMetadata[service][dayIndex]) {
        site.urbantzMetadata[service][dayIndex] = {};
    }

    // Only set the value if it's explicitly true or false
    const lowered = value?.toLowerCase().trim();
    if (lowered === 'true') {
        site.urbantzMetadata[service][dayIndex][weekType] = true;
    } else if (lowered === 'false') {
        site.urbantzMetadata[service][dayIndex][weekType] = false;
    } else {
        // If value is empty or invalid, set to undefined to keep existing value
        site.urbantzMetadata[service][dayIndex][weekType] = undefined;
    }
};

export function formatSiteCsvData({ csvData }: { csvData: CsvDataRow[] }) {
    const [headerValues, ...rawData] = csvData;
    const data = rawData.filter((row) => {
        return row.some((cell) => cell.trim() !== '');
    });
    const headers = headerValues.map((value) => value.toLowerCase().trim());

    const allFields = [...REQUIRED_UPDATE_FIELDS, ...OPTIONAL_UPDATE_FIELDS].map((field) => ({
        name: field.name.toLowerCase(),
        original: field.name,
    }));

    return data.map((row) => {
        const site: Record<string, any> = {};
        const deliveryDaysMap: Record<string, boolean | undefined> = {};
        const freshReassortDaysMap: Record<string, boolean | undefined> = {};
        const dryReassortDaysMap: Record<string, boolean | undefined> = {};

        headers.forEach((header, index) => {
            const value = row[index]?.trim();
            if (value === undefined) return;

            const field = allFields.find((f) => f.name === header);
            if (!field) return;

            const fieldPath = field.original.split('.');

            // Special handling for delivery days
            if (fieldPath[0] === 'deliveryDays') {
                // Only allow dot notation format (deliveryDays.monday, etc.)
                if (fieldPath.length !== 2) return;

                const day = fieldPath[1];
                // Validate day name
                if (!['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].includes(day))
                    return;

                const lowered = value.toLowerCase().trim();
                if (isTrue(lowered)) deliveryDaysMap[day] = true;
                else if (isFalse(lowered)) deliveryDaysMap[day] = false;
                return;
            }

            // Special handling for freshReassortDays
            if (fieldPath[0] === 'freshReassortDays') {
                if (fieldPath.length !== 2) return;
                const day = fieldPath[1];
                if (!['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].includes(day))
                    return;
                const lowered = value.toLowerCase().trim();
                if (isTrue(lowered)) freshReassortDaysMap[day] = true;
                else if (isFalse(lowered)) freshReassortDaysMap[day] = false;
                return;
            }

            // Special handling for dryReassortDays
            if (fieldPath[0] === 'dryReassortDays') {
                if (fieldPath.length !== 2) return;
                const day = fieldPath[1];
                if (!['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].includes(day))
                    return;
                const lowered = value.toLowerCase().trim();
                if (isTrue(lowered)) dryReassortDaysMap[day] = true;
                else if (isFalse(lowered)) dryReassortDaysMap[day] = false;
                return;
            }

            // Special handling for urbantzMetadata
            if (fieldPath[0] === 'urbantzMetadata' && fieldPath.length === 4) {
                handleUrbantzMetadata(site, fieldPath, value);
                return;
            }

            const formattedValue = formatField(value, field.original);
            if (formattedValue !== undefined) {
                if (fieldPath.length > 1) {
                    handleNestedField(site, fieldPath, formattedValue);
                } else {
                    site[field.original] = formattedValue;
                }
            }
        });

        // Convert deliveryDays object to array if any days were specified
        if (Object.keys(deliveryDaysMap).length > 0) {
            const existingDeliveryDays = new Array(7).fill(undefined);
            if ('monday' in deliveryDaysMap) existingDeliveryDays[0] = deliveryDaysMap.monday;
            if ('tuesday' in deliveryDaysMap) existingDeliveryDays[1] = deliveryDaysMap.tuesday;
            if ('wednesday' in deliveryDaysMap) existingDeliveryDays[2] = deliveryDaysMap.wednesday;
            if ('thursday' in deliveryDaysMap) existingDeliveryDays[3] = deliveryDaysMap.thursday;
            if ('friday' in deliveryDaysMap) existingDeliveryDays[4] = deliveryDaysMap.friday;
            if ('saturday' in deliveryDaysMap) existingDeliveryDays[5] = deliveryDaysMap.saturday;
            if ('sunday' in deliveryDaysMap) existingDeliveryDays[6] = deliveryDaysMap.sunday;

            site.deliveryDays = existingDeliveryDays;
        }

        // Convert freshReassortDays object to array if any days were specified
        if (Object.keys(freshReassortDaysMap).length > 0) {
            const arr = new Array(7).fill(undefined);
            if ('monday' in freshReassortDaysMap) arr[0] = freshReassortDaysMap.monday;
            if ('tuesday' in freshReassortDaysMap) arr[1] = freshReassortDaysMap.tuesday;
            if ('wednesday' in freshReassortDaysMap) arr[2] = freshReassortDaysMap.wednesday;
            if ('thursday' in freshReassortDaysMap) arr[3] = freshReassortDaysMap.thursday;
            if ('friday' in freshReassortDaysMap) arr[4] = freshReassortDaysMap.friday;
            if ('saturday' in freshReassortDaysMap) arr[5] = freshReassortDaysMap.saturday;
            if ('sunday' in freshReassortDaysMap) arr[6] = freshReassortDaysMap.sunday;

            site.freshReassortDays = arr;
        }

        // Convert dryReassortDays object to array if any days were specified
        if (Object.keys(dryReassortDaysMap).length > 0) {
            const arr = new Array(7).fill(undefined);
            if ('monday' in dryReassortDaysMap) arr[0] = dryReassortDaysMap.monday;
            if ('tuesday' in dryReassortDaysMap) arr[1] = dryReassortDaysMap.tuesday;
            if ('wednesday' in dryReassortDaysMap) arr[2] = dryReassortDaysMap.wednesday;
            if ('thursday' in dryReassortDaysMap) arr[3] = dryReassortDaysMap.thursday;
            if ('friday' in dryReassortDaysMap) arr[4] = dryReassortDaysMap.friday;
            if ('saturday' in dryReassortDaysMap) arr[5] = dryReassortDaysMap.saturday;
            if ('sunday' in dryReassortDaysMap) arr[6] = dryReassortDaysMap.sunday;

            site.dryReassortDays = arr;
        }

        return site;
    });
}
